import { useEffect, useMemo, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { PublicUser } from '@avslack/shared';
import { messagePlainText, type LocalMessage } from '../data/messages';
import { PRIMARY_REACTIONS } from '../data/emoji';
import { ReactionBar, EmojiPicker } from './ReactionBar';
import { jumboEmojiCount } from './Emoji';
import { useCustomEmoji } from '../data/custom-emoji';
import { RichText } from './RichText';
import { Attachments } from './Attachments';
import { LinkPreviews } from './LinkPreviews';
import { MessageHistory } from './MessageHistory';
import { MessageActionButtons } from './MessageActions';
import { PollCard } from './PollCard';
import { RichComposer, type ComposerSubmit } from './RichComposer';
import { useGroups } from '../data/workspace';
import { Icon } from './Icon';
import { useUsers } from '../data/users';
import { useQuery } from '@tanstack/react-query';
import { getMessage } from '../lib/api';
import { useHasHover } from '../lib/media';
import { useLongPress } from '../lib/long-press';
import { MessageSheet } from './MessageSheet';
import { MAX_FORWARD_MESSAGES, useMessageSelection } from '../lib/message-selection';

export interface MessageActions {
  onToggleReaction: (messageId: string, emoji: string) => void;
  onEdit: (messageId: string, value: ComposerSubmit) => Promise<void> | void;
  onDelete: (messageId: string) => void;
  onRetry: (message: LocalMessage) => void;
  /** Kimeno sorban varakozo uzenet elvetese (K2). */
  onDiscard?: (message: LocalMessage) => void;
  /**
   * Valasz inditasa: a MESSENGER mintaja szerint az idezet a fo szerkeszto
   * fole kerul, es a valasz a folyam vegen jelenik meg - nem a szulo alatt.
   */
  onStartReply: (message: LocalMessage) => void;
  onTogglePin: (messageId: string) => void;
  /**
   * FONTOS jeloles - utolag, barmelyik uzeneten.
   *
   * Nem csak a sajaton: hogy valamit ne gorgessen at senki, azt gyakran az
   * OLVASO veszi eszre, nem az iro. A levetel ugyanezen az uton megy.
   */
  onToggleImportant: (messageId: string, isImportant: boolean) => void;
  /** FELADAT-jeloles (utolag is) es kipipalas. */
  onToggleTask?: (messageId: string, isTask: boolean) => void;
  onToggleTaskDone?: (messageId: string, done: boolean) => void;
  onToggleSaved: (messageId: string) => void;
  onCopyLink: (messageId: string) => void;
  /** Az uzenet SZOVEGE a vagolapra - a folyam fole ugro gombsorbol. */
  onCopyText: (message: LocalMessage) => void;
  onForward: (message: LocalMessage) => void;
  /** Tobb uzenet kijelolese tovabbitashoz (U5) - ezzel az uzenettel indul. */
  onSelectForForward?: (message: LocalMessage) => void;
  /**
   * UJRAKULDES: a SAJAT uzenet masolata ujkent, ugyanabba a csatornaba.
   *
   * A mellekletek NEM toltodnek fel ujra: a masolat ugyanazokra a fajlokra
   * hivatkozik (egy fajl tobb uzenethez is tartozhat). Csak a sajat uzenetre
   * van - masenel a tovabbitas a helyes ut, mert az forrast is jelol, ez
   * viszont ugy tenne, mintha mi irtuk volna.
   */
  onResend: (message: LocalMessage) => void;
  /**
   * Feladat a ProjectFlow-bol.
   *
   * `null`, ha a cegnel nincs bekapcsolva az integracio - ilyenkor a menupont
   * sem jelenik meg. Nem elrejtett funkcio: nem letezik.
   */
  onProjectflow: ((message: LocalMessage) => void) | null;
  /**
   * Olvatlanna jeloles ettol az uzenettol. `null`, ha nem vagy tagja a
   * csatornanak - akkor nincs is sajat olvasottsagod.
   */
  onMarkUnread: ((message: LocalMessage) => void) | null;
}

interface MessageRowProps {
  message: LocalMessage;
  meId: string;
  usersById: Map<string, PublicUser>;
  canManage: boolean;
  actions: MessageActions;
  /** A betoltott uzenetek - az idezet ebbol keresi ki a szulot. */
  loadedById: Map<string, LocalMessage>;
  onJumpTo: (messageId: string) => void;
  /** Permalinkkel ide ugrottunk: gorgetes + atmeneti kiemeles. */
  highlighted?: boolean;
  /** Kuldes / elkuldve / latta - csak a sajat uzeneteknel, a hivo dolga. */
  status?: React.ReactNode;
}

/**
 * A nagyitott emoji merete darabszam szerint.
 *
 * Az osztalynevek SZO SZERINT szerepelnek, kulonben a Tailwind nem generalna
 * le oket.
 */
const JUMBO: Record<number, string> = {
  1: 'text-[2.75rem] leading-tight',
  2: 'text-[2.25rem] leading-tight',
  3: 'text-[1.75rem] leading-tight',
};

export function MessageRow({
  message,
  meId,
  usersById,
  canManage,
  actions,
  loadedById,
  onJumpTo,
  highlighted = false,
  status = null,
}: MessageRowProps) {
  const [editing, setEditing] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  /*
    A KIEMELT UZENET KOZEPRE KERUL - es ott is MARAD, amig a tartalom el nem rendezodik.

    Korabban egyetlen, animalt scrollIntoView futott a kiemeles pillanataban. Ezt ket dolog
    elrontotta: a lista aljara tapadasa felulirta (a csatorna aljan kotottel ki), es a KESOBB
    betolto tartalom (link-elonezet, kep, a fole betoltott korabbi uzenetek) eltolta - az uzenet a nezet
    szelere csuszott. Most elobb azonnal kozepre allunk, majd amig a kiemeles tart, minden
    magassag-valtozasnal ujra kozepre igazitunk - kiveve, ha a felhasznalo kozben maga
    gorgetett (gorgo, erintes, billentyu, gorgetosav): akkor nem rantjuk vissza.
  */
  useEffect(() => {
    if (!highlighted) return;
    const root = rootRef.current;
    if (!root) return;

    let scroller: HTMLElement | null = root.parentElement;
    while (scroller) {
      const overflow = getComputedStyle(scroller).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') break;
      scroller = scroller.parentElement;
    }
    if (!scroller) {
      root.scrollIntoView({ block: 'center' });
      return;
    }
    const box = scroller;

    let userMoved = false;
    const stop = () => {
      userMoved = true;
    };
    const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
    for (const name of events) box.addEventListener(name, stop, { passive: true });

    const center = () => {
      if (userMoved || !root.isConnected) return;
      const message = root.getBoundingClientRect();
      const view = box.getBoundingClientRect();
      const offset = message.top - view.top - (view.height - message.height) / 2;
      if (Math.abs(offset) > 1) box.scrollTop += offset;
    };

    center();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => center());
    // A gorgetheto elem KOZVETLEN gyerekeit figyeljuk: ezek novekednek, ha valami betolt.
    if (observer) for (const child of Array.from(box.children)) observer.observe(child);
    // Egy kis kesleltetett ujraigazitas azokra a tartalmakra, amik meresi esemeny nelkul ereznek.
    const late = window.setTimeout(center, 600);

    return () => {
      window.clearTimeout(late);
      observer?.disconnect();
      for (const name of events) box.removeEventListener(name, stop);
    };
  }, [highlighted]);
  const isMine = message.userId === meId;
  const deleted = Boolean(message.deletedAt);
  /** Nyitva van-e a "(szerkesztve)" mogotti elozmeny. */
  const [historyOpen, setHistoryOpen] = useState(false);

  /*
    Hover VAN vagy NINCS - ez donti el, hogyan lehet a muveletekhez jutni.

    Egerrel a lebego sav a jo: csak akkor latszik, amikor az uzeneten allsz.
    Erintessel viszont nincs hover, tehat a sav gyakorlatilag elerhetetlen -
    ott a hosszu nyomas hozza elo ugyanezeket, egy also lapon.
  */
  /*
    Csak emojibol all-e az uzenet - es hanybol.

    A sajat emojik felismeresehez kell a keszlet, ezert a horog itt van, nem a
    segedfuggvenyben (lasd `jumboEmojiCount`).
  */
  const { byName: customEmoji } = useCustomEmoji();
  const jumbo = deleted ? 0 : jumboEmojiCount(messagePlainText(message), customEmoji);

  const hasHover = useHasHover();
  const selection = useMessageSelection();
  const { nameOf } = useUsers();
  const interactive = !editing && !message.pending && !deleted;
  const longPress = useLongPress(() => setSheetOpen(true), !hasHover && interactive);

  if (deleted) {
    return <p className="py-0.5 text-sm italic text-ink-faint">Ez az üzenet törölve lett.</p>;
  }

  const isSelected = selection.selected.includes(message.id);
  const selectable = selection.active && interactive;
  const selectionFull = selection.selected.length >= MAX_FORWARD_MESSAGES && !isSelected;

  return (
    <div
      ref={rootRef}
      {...(hasHover || selection.active ? {} : longPress)}
      /*
        KIJELOLO MODBAN (U5) az egesz sor jelol: a kattintas nem nyit linket,
        nem indit menut - kulonben a kijeloles felbe-szakadna.
      */
      {...(selectable
        ? {
            onClickCapture: (event: React.MouseEvent) => {
              event.preventDefault();
              event.stopPropagation();
              if (!selectionFull) selection.toggle(message.id);
            },
          }
        : {})}
      className={[
        'group/msg',
        'relative w-full',
        message.pending ? 'opacity-65' : '',
        selectable ? `cursor-pointer pl-8 ${selectionFull ? 'opacity-60' : ''}` : '',
      ].join(' ')}
    >
      {selectable && (
        <input
          type="checkbox"
          readOnly
          checked={isSelected}
          disabled={selectionFull}
          aria-label={isSelected ? 'Kijelölés megszüntetése' : 'Kijelölés továbbításhoz'}
          className="absolute left-1 top-3 size-4 accent-[var(--accent)]"
        />
      )}
      {/*
        A HOSSZU NYOMAST magatol senki nem talalja ki.

        Ezert erintokepernyon ott all egy halvany harom pont is: ugyanazt a
        lapot nyitja. Egerrel NEM jelenik meg - ott a hover-sav a jarhato ut,
        es egy allando gomb minden uzeneten csak zaj lenne.
      */}
      {interactive && !hasHover && !selection.active && (
        <button
          type="button"
          aria-label="Üzenet műveletei"
          onClick={() => setSheetOpen(true)}
          className="absolute right-0 top-0 grid size-7 place-items-center rounded text-ink-faint/60 active:bg-surface-hover active:text-ink"
        >
          ⋯
        </button>
      )}
      {/*
        EZ A DOBOZ - TELJES SZELESSEGGEL.

        Nem a tartalomhoz igazodik: minden uzenet ugyanolyan szeles dobozt kap.
        Igy a dobozok bal ES jobb szele is egy vonalban all, a lebego akciosav
        pedig mindig ugyanoda kerul - nem ugral uzenetenkent aszerint, hogy
        hany szo van benne.
      */}
      <div
        className={[
          'w-full rounded-card px-3 py-2',
          'transition-colors duration-(--duration-base)',
          message.isImportant
            ? /*
                FONTOS uzenet: maga a BUBOREK kiemelt.

                A kiemeles arra az egy uzenetre szol, amelyikre a kuldo
                gondolta - nem az egesz futamra. Ezert nem is tori meg a
                futamot (lasd `partition`).
              */
              'border border-accent bg-accent-soft'
            : 'border border-border-soft bg-surface-raised',
          highlighted ? 'ring-2 ring-alert' : '',
          /*
            Erintesen a hosszu nyomas a MIENK.

            Enelkul a bongeszo sajat szovegkijeloloje (es iOS-en a nagyito)
            indulna el ugyanarra a mozdulatra, a menu folott. A szoveg igy sem
            vesz el: a lapon ott a "Szöveg másolása".
          */
          hasHover ? '' : 'select-none [-webkit-touch-callout:none]',
          // Helyet hagyunk az erintos harom pontnak.
          hasHover ? '' : 'pr-8',
        ].join(' ')}
      >
        {(message.isImportant || message.isPinned || message.isSaved) && (
          <div className="mb-1 flex items-center gap-2 text-2xs font-medium text-ink-faint">
            {/*
            A CIMKE a buborek tetejen, nem a sarkaba lebegtetve.

            Egy lebego cimke egy keskeny buborekon rautne a szovegre; igy
            viszont barmilyen szeles is a doboz, a cimke a helyen marad. A kek
            hatter magaban is feltunik, de nem mondja meg, MIERT mas ez az
            uzenet - egy szinnek nincs jelentese, amig valaki meg nem tanulja.
          */}
            {message.isImportant && (
              <span className="flex items-center gap-1 rounded-pill bg-accent px-1.5 py-px font-semibold text-accent-ink">
                <span aria-hidden="true">
                  <Icon name="figyelmeztetes" size="sm" />
                </span>
                Fontos
              </span>
            )}
            {message.isPinned && (
              <span className="flex items-center gap-1 text-alert">
                <span aria-hidden="true">
                  <Icon name="kituzes" />
                </span>{' '}
                Kitűzve
              </span>
            )}
            {message.isSaved && (
              <span className="flex items-center gap-1 text-accent">
                <span aria-hidden="true">
                  <Icon name="mentes" />
                </span>{' '}
                Mentve
              </span>
            )}
          </div>
        )}

        {/*
        MIRE VALASZ.

        A valasz a folyam vegen all, nem a szuloje alatt - tehat magaval kell
        hoznia a kontextust. Ez a sor mondja el, kinek es mire valaszol; ra
        kattintva a beszelgetes odagordul.
      */}
        {message.parentId && !editing && (
          <QuotedParent
            parentId={message.parentId}
            loadedById={loadedById}
            usersById={usersById}
            onJumpTo={onJumpTo}
          />
        )}

        {/*
          SZAVAZAS (U4): a kartya a szoveg HELYETT all, a prose-dobozon kivul
          (annak listastilusa a kartya opcioira is rahuzna). A szoveg (kerdes
          es opciok) a kereseshez es az ertesiteshez kell, itt ismetles lenne.
        */}
        {typeof message.meta?.pollId === 'string' && !message.deletedAt && !editing ? (
          <PollCard pollId={message.meta.pollId} />
        ) : editing ? (
          <InlineEditor
            message={message}
            usersById={usersById}
            meId={meId}
            onCancel={() => setEditing(false)}
            onSave={async (value) => {
              await actions.onEdit(message.id, value);
              setEditing(false);
            }}
          />
        ) : (
          /*
          A CSAK EMOJIBOL allo uzenet NAGYOBB - ahogy a Messengerben.

          Egy magaban allo 👍 nem mondat, hanem gesztus: a szoveg meretere
          zsugoritva elveszik a folyamban, pedig eppen az a dolga, hogy egy
          pillantasbol latszodjon. A meret a darabszammal csokken - harom jel
          mar sorba rendezodik, es a legnagyobb meretben kilogna a kartyabol.

          A sajat (`:nev:`) emojik is nagyodnak: a kepuk `em`-ben van megadva,
          tehat a betumerettel egyutt no.
        */
          <div className={`avslack-prose break-words ${JUMBO[jumbo] ?? ''}`}>
            {/* A regi, sima szoveges uzeneteknek (bot, webhook) nincs dokumentumuk. */}
            {message.body ? (
              <RichText doc={message.body} meId={meId} usersById={usersById} />
            ) : (
              <p className="whitespace-pre-wrap">{message.text}</p>
            )}
            {/*
              A "(szerkesztve)" jelzes KATTINTHATO: mogotte az elozmeny.

              Magaban a jelzes csak annyit mond, hogy valami megvaltozott - azt
              nem, hogy MI. Aki kesobb olvassa a beszelgetest, igy nem tudja
              eldonteni, hogy amire valaszoltak, ugyanaz volt-e, mint amit most
              lat.
            */}
            {message.editedAt && (
              <button
                type="button"
                onClick={() => setHistoryOpen((open) => !open)}
                aria-expanded={historyOpen}
                title="Korábbi változatok megtekintése"
                className="ml-1.5 align-baseline text-2xs text-ink-faint underline decoration-dotted underline-offset-2 transition-colors hover:text-ink"
              >
                (szerkesztve)
              </button>
            )}
            {/*
            Az OLELESBOL erkezett uzenet meg van jelolve.

            A hivas chatje a csatornaba mentodik - ez a lenyege: a hivas vege
            nem torol semmit. De a kontextus nelkul furcsan hatna, hogy valaki
            egy fel mondatot ir be a semmibe; a jelolesbol latszik, hogy egy
            beszelgetes kozben hangzott el.
          */}
            {/* Az MCP-n at, MI altal kuldott uzenet (M6) - a kuldo neveben, de jelolve. */}
            {message.meta?.via === 'mcp' && (
              <span
                title="Egy MI küldte a feladó nevében, az ő engedélyével"
                className="ml-1.5 align-middle text-2xs text-ink-faint"
              >
                ✦ MI-n keresztül
              </span>
            )}
            {typeof message.meta?.huddleId === 'string' && (
              <span
                title="Az ölelés chatjéből"
                className="ml-1.5 align-middle text-2xs text-ink-faint"
              >
                <Icon name="huddle" size="sm" /> ölelésből
              </span>
            )}
          </div>
        )}

        {/*
          Az elozmeny a prose-konteneren KIVUL all: nem uzenet-tartalom, es a
          `.avslack-prose` listastilusa egyebkent rasegitene egy folosleges
          sorszamot a sajat cimkeink melle.
        */}
        {historyOpen && message.editedAt && (
          <MessageHistory
            messageId={message.id}
            meId={meId}
            usersById={usersById}
            onClose={() => setHistoryOpen(false)}
          />
        )}

        {/* Integracio gombjai - a `meta.actions` mezobol. */}
        <MessageActionButtons messageId={message.id} meta={message.meta} />

        <LinkPreviews links={message.links} />

        <Attachments files={message.files} />

        {/* FELADAT: kipipalhato cimke az uzenet alatt; kesz allapotban ki es mikor. */}
        {message.task && (
          <label
            onClick={(event) => event.stopPropagation()}
            className={[
              'mt-1.5 inline-flex cursor-pointer items-center gap-1.5 rounded-pill border px-2 py-0.5 text-2xs font-medium',
              message.task.done
                ? 'border-border-soft text-ink-faint'
                : 'border-accent bg-accent-soft text-accent',
            ].join(' ')}
          >
            <input
              type="checkbox"
              checked={message.task.done}
              disabled={!actions.onToggleTaskDone || message.pending}
              onChange={(event) => actions.onToggleTaskDone?.(message.id, event.target.checked)}
              className="size-3 accent-[var(--accent)]"
            />
            {message.task.done
              ? `Elvégezve${message.task.doneBy ? ` – ${nameOf(message.task.doneBy)}` : ''}${
                  message.task.doneAt
                    ? `, ${new Date(message.task.doneAt).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}`
                    : ''
                }`
              : 'Feladat'}
          </label>
        )}
      </div>

      {/*
        A REAKCIOK a buborek ALATT allnak, nem benne.

        Beluluk egy egyszavas uzenet doboza a reakciosor szelessegere nyult
        volna: a "ok" egy tenyerni dobozban, alatta harom jelvennyel. A
        buborek a TARTALOMHOZ igazodik; a reakcio nem tartalom, hanem valasz ra.
      */}
      <ReactionBar
        reactions={message.reactions}
        meId={meId}
        onToggle={(emoji) => actions.onToggleReaction(message.id, emoji)}
      />

      {status}

      {message.failed && (
        /*
          A HIBA OKA is ide tartozik.

          Korabban csak annyi allt itt, hogy "Nem sikerult elkuldeni" - az Ujra
          gomb pedig ugyanabba a hibaba futott, ahanyszor csak megnyomtak. Aki
          ezt latta, joggal hitte, hogy az alkalmazas veletlenszeruen elnyel
          uzeneteket. A szerver kozben pontosan megmondta, mi a baj; csak
          senki nem tovabbitotta.
        */
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-danger">
          <span role="alert">{message.failedReason ?? 'Nem sikerült elküldeni.'}</span>
          <button
            type="button"
            onClick={() => actions.onRetry(message)}
            className="rounded px-1.5 py-0.5 font-medium underline underline-offset-2 hover:bg-danger-soft"
          >
            Újra
          </button>
          {actions.onDiscard && (
            <button
              type="button"
              onClick={() => actions.onDiscard?.(message)}
              className="rounded px-1.5 py-0.5 font-medium underline underline-offset-2 hover:bg-danger-soft"
            >
              Elvetés
            </button>
          )}
        </div>
      )}

      {interactive &&
        !selection.active &&
        (hasHover ? (
          <HoverActions
            message={message}
            isMine={isMine}
            canManage={canManage}
            onReact={(emoji) => actions.onToggleReaction(message.id, emoji)}
            onReply={() => actions.onStartReply(message)}
            onEdit={() => setEditing(true)}
            actions={actions}
          />
        ) : (
          <MessageSheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            message={message}
            isMine={isMine}
            canManage={canManage}
            actions={actions}
            onReact={(emoji) => actions.onToggleReaction(message.id, emoji)}
            onReply={() => actions.onStartReply(message)}
            onEdit={() => setEditing(true)}
          />
        ))}
    </div>
  );
}

/**
 * Lebego akciosav - csak hoverre es fokuszra latszik, hogy ne zajositsa a folyamot.
 *
 * A gyakori muveletek (reakcio, valasz) egy kattintasra vannak; a ritkabbak
 * legordulo menube kerultek. Nyolc ikon egymas mellett mar nem sav, hanem zaj.
 */
function HoverActions({
  message,
  isMine,
  canManage,
  onReact,
  onReply,
  onEdit,
  actions,
}: {
  message: LocalMessage;
  isMine: boolean;
  canManage: boolean;
  onReact: (emoji: string) => void;
  onReply: () => void;
  onEdit: () => void;
  actions: MessageActions;
}) {
  // A ket osztalynev SZO SZERINT szerepel, kulonben a Tailwind nem generalna le.
  /*
    A sav a BUBOREK jobb felso sarkara ul, kisse ratakarva.

    Korabban `-top-3.5` allt itt: az akkori teljes szelessegu kartyanal ez a
    kartya folotti uresbe emelte a savot. Buborekoknal viszont a savot a
    FELETTE allo buborekba lokte - egy keskeny uzenetnel raadasul balra is
    kilogott, mert a sav szelesebb, mint maga a buborek. Egy rovid atfedes a
    sajat buborekjaval pontosan annyi, amennyi kell.
  */
  const base =
    'absolute -top-2 right-0 z-10 flex items-center gap-0.5 rounded-control border border-border-soft bg-surface-raised p-0.5 opacity-0 shadow-(--shadow-lift) transition-opacity duration-(--duration-quick) focus-within:opacity-100';

  return (
    <div className={`${base} group-hover/msg:opacity-100`}>
      {PRIMARY_REACTIONS.map((emoji) => (
        <ActionButton key={emoji} label={`Reakció: ${emoji}`} onClick={() => onReact(emoji)}>
          {emoji}
        </ActionButton>
      ))}

      <EmojiPicker onPick={onReact}>
        <button
          type="button"
          title="További reakciók"
          aria-label="További reakciók"
          className="grid size-7 place-items-center rounded text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
        >
          <Icon name="emoji" />
        </button>
      </EmojiPicker>

      <ActionButton label="Válasz erre az üzenetre" onClick={onReply}>
        <Icon name="valasz" />
      </ActionButton>

      {/*
        SZOVEG MASOLASA - egy kattintassal, nem a "tovabbi muveletek" mogul.

        Gyakori mozdulat: valaki bemasol egy hibauzenetet vagy egy cimet, es
        azt tovabb kell vinni mashova. Kijelolessel is menne, de a folyamban
        konnyen tul- vagy alulhuzza az ember.

        Csak akkor jelenik meg, ha VAN mit masolni: egy csak-kepbol allo
        uzenetnel a gomb ures vagolapot adna.
      */}
      {messagePlainText(message) !== '' && (
        <ActionButton label="Szöveg másolása" onClick={() => actions.onCopyText(message)}>
          <Icon name="masolas" />
        </ActionButton>
      )}

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            title="További műveletek"
            aria-label="További műveletek"
            className="grid size-7 place-items-center rounded text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
          >
            ⋯
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="animate-fade-rise z-50 min-w-48 rounded-card border border-border-soft bg-surface-raised p-1 shadow-(--shadow-modal)"
          >
            {actions.onToggleTask && (
              <MenuItem onSelect={() => actions.onToggleTask?.(message.id, !message.task)}>
                <span aria-hidden="true">
                  <Icon name="pipa" />
                </span>
                {message.task ? 'Feladat-jelölés levétele' : 'Megjelölés feladatként'}
              </MenuItem>
            )}

            <MenuItem onSelect={() => actions.onToggleImportant(message.id, !message.isImportant)}>
              <span aria-hidden="true">
                <Icon name="figyelmeztetes" />
              </span>
              {message.isImportant ? 'Fontos jelölés levétele' : 'Megjelölés fontosként'}
            </MenuItem>

            <MenuItem onSelect={() => actions.onTogglePin(message.id)}>
              <span aria-hidden="true">
                <Icon name="kituzes" />
              </span>
              {message.isPinned ? 'Kitűzés levétele' : 'Kitűzés a csatornában'}
            </MenuItem>

            <MenuItem onSelect={() => actions.onToggleSaved(message.id)}>
              <span aria-hidden="true">
                <Icon name="mentes" />
              </span>
              {message.isSaved ? 'Mentés visszavonása' : 'Mentés későbbre'}
            </MenuItem>

            {actions.onMarkUnread && !message.pending && message.seq > 0 && (
              <MenuItem onSelect={() => actions.onMarkUnread?.(message)}>
                <span aria-hidden="true">
                  <Icon name="rejt" />
                </span>
                Olvasatlanná jelölés innen
              </MenuItem>
            )}

            <MenuItem onSelect={() => actions.onCopyLink(message.id)}>
              <span aria-hidden="true">⧉</span>
              Link másolása
            </MenuItem>

            <MenuItem onSelect={() => actions.onForward(message)}>
              <span aria-hidden="true">
                <Icon name="tovabbitas" />
              </span>
              Továbbítás
            </MenuItem>

            {actions.onSelectForForward && (
              <MenuItem onSelect={() => actions.onSelectForForward?.(message)}>
                <span aria-hidden="true">☑</span>
                Több üzenet továbbítása…
              </MenuItem>
            )}

            {actions.onProjectflow && (
              <MenuItem onSelect={() => actions.onProjectflow?.(message)}>
                <span aria-hidden="true">
                  <Icon name="pipa" />
                </span>
                Küldés a ProjectFlow-ba
              </MenuItem>
            )}

            {(isMine || canManage) && (
              <DropdownMenu.Separator className="my-1 h-px bg-border-soft" />
            )}

            {isMine && !message.failed && (
              <MenuItem onSelect={() => actions.onResend(message)}>
                <span aria-hidden="true">
                  <Icon name="kuldes" />
                </span>
                Újraküldés
              </MenuItem>
            )}

            {isMine && (
              <MenuItem onSelect={onEdit}>
                <span aria-hidden="true">
                  <Icon name="szerkesztes" />
                </span>
                Szerkesztés
              </MenuItem>
            )}

            {(isMine || canManage) && (
              <MenuItem danger onSelect={() => actions.onDelete(message.id)}>
                <Icon name="torles" />
                Törlés
              </MenuItem>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

function MenuItem({
  danger,
  onSelect,
  children,
}: {
  danger?: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={[
        'flex cursor-pointer items-center gap-2.5 rounded-control px-2.5 py-1.5 text-sm outline-none',
        danger
          ? 'text-danger data-[highlighted]:bg-danger-soft'
          : 'data-[highlighted]:bg-surface-hover',
      ].join(' ')}
    >
      {children}
    </DropdownMenu.Item>
  );
}

/**
 * MIRE VALASZ - a valasz fole tett idezet.
 *
 * A valasz a folyam vegen all, nem a szuloje alatt, tehat magaval kell hoznia
 * a kontextust: kinek es mire valaszol. Ra kattintva a beszelgetes odagordul.
 *
 * ─── Ha a szulo nincs betoltve ──────────────────────────────────────────────
 *
 * Egy ket hete irt uzenetre adott valasznal a szulo tipikusan kivul van a
 * lapozott ablakon. Ilyenkor KULON lekerdezzuk - ez a ritkabb eset, es sokkal
 * jobb, mint egy "az eredeti nem elerheto" felirat ott, ahol a lenyeg lenne.
 */
function QuotedParent({
  parentId,
  loadedById,
  usersById,
  onJumpTo,
}: {
  parentId: string;
  loadedById: Map<string, LocalMessage>;
  usersById: Map<string, PublicUser>;
  onJumpTo: (messageId: string) => void;
}) {
  const { nameOf } = useUsers();
  const helyben = loadedById.get(parentId);

  const tavoli = useQuery({
    queryKey: ['message', parentId],
    queryFn: () => getMessage(parentId),
    enabled: !helyben,
    staleTime: 5 * 60_000,
  });

  const szulo = helyben ?? tavoli.data;

  const szoveg = szulo
    ? szulo.deletedAt
      ? 'Törölt üzenet'
      : messagePlainText(szulo) || osszefoglalo(szulo)
    : tavoli.isError
      ? 'Az eredeti üzenet nem tölthető be.'
      : '…';

  return (
    <button
      type="button"
      onClick={() => szulo && onJumpTo(szulo.id)}
      disabled={!szulo}
      title={szulo ? 'Ugrás az eredeti üzenetre' : undefined}
      className="mb-1 flex w-full max-w-lg items-center gap-1.5 rounded-control border-l-2 border-border-strong bg-surface-sunken/70 py-1 pl-2 pr-2.5 text-left text-2xs text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover enabled:hover:text-ink-muted"
    >
      <span aria-hidden="true" className="shrink-0">
        <Icon name="valasz" size="sm" />
      </span>
      <span className="shrink-0 font-semibold">
        {szulo ? nameOf(szulo.userId ? usersById.get(szulo.userId) : undefined) : ''}
      </span>
      <span className="min-w-0 truncate">{szoveg}</span>
    </button>
  );
}

/** Szoveg nelkuli uzenet (kep, fajl): legyen mit kiirni az idezetben. */
function osszefoglalo(message: LocalMessage): string {
  if (message.files.length > 0) {
    return message.files.length === 1 ? 'Melléklet' : `${message.files.length} melléklet`;
  }
  return 'Üzenet';
}

function ActionButton({
  label,
  danger,
  onClick,
  children,
}: {
  label: string;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={[
        'grid size-7 place-items-center rounded text-sm transition-colors duration-(--duration-quick)',
        danger
          ? 'text-ink-faint hover:bg-danger-soft hover:text-danger'
          : 'text-ink-faint hover:bg-surface-hover hover:text-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function InlineEditor({
  message,
  usersById,
  meId,
  onSave,
  onCancel,
}: {
  message: LocalMessage;
  usersById: Map<string, PublicUser>;
  meId: string;
  onSave: (value: ComposerSubmit) => Promise<void> | void;
  onCancel: () => void;
}) {
  const users = useMemo(() => [...usersById.values()], [usersById]);
  const { data: groups } = useGroups();
  const initial = message.body ?? undefined;

  return (
    <div>
      <div className="rounded-control border border-accent bg-surface px-2.5 py-1.5">
        <RichComposer
          compact
          autoFocus
          placeholder="Szerkesztés…"
          users={users}
          groups={groups ?? []}
          meId={meId}
          initial={initial}
          submitLabel="Mentés"
          onSubmit={onSave}
          onCancel={onCancel}
        />
      </div>
      <div className="mt-1.5 flex items-center gap-2 text-2xs text-ink-faint">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-1.5 py-0.5 underline underline-offset-2 hover:bg-surface-hover hover:text-ink"
        >
          Mégse
        </button>
        <span>Enter ment · Esc kilép</span>
      </div>
    </div>
  );
}
